import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styled, { keyframes, css } from "styled-components";
import { MdClear } from "react-icons/md";
import { color } from "src/styles/variables";

const modalRoot = document.getElementById("modal-root");

function closeOnEsc(cb) {
  return (event) => {
    if (event.keyCode === 27) {
      cb();
    }
  };
}

function Modal({
  closeModal,
  children,
  smallFullScreen,
  maxWidth = 650,
  variant,
  ...props
}) {
  const [showModal, setShowModal] = useState(true);

  useEffect(() => {
    if (closeModal) {
      const closeOnEscHandler = closeOnEsc(closeModal);
      document.body.style.overflow = "hidden";
      document.getElementById("react-modal-component").focus();
      document.addEventListener("keydown", closeOnEscHandler);

      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", closeOnEscHandler);
      };
    }
  }, [closeModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => {
      closeModal();
    }, 100);
  };

  return createPortal(
    <ModalWrapper onClick={closeModal ? handleCloseModal : null}>
      <ModalContainer
        {...props}
        showModal={showModal}
        data-selector={props["data-selector"] || "Modal"}
        id="react-modal-component"
        tabIndex="-1"
        role="dialog"
        variant={variant}
        maxWidth={maxWidth}
        smallFullScreen={smallFullScreen}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <ModalHeader variant={variant}>
          {closeModal && (
            <ModalClose
              variant={variant}
              onClick={handleCloseModal}
              data-selector="Modal-close-button"
              type="button"
            >
              <MdClear
                size={variant === "image" ? 25 : 30}
                style={{ verticalAlign: "middle" }}
              />
            </ModalClose>
          )}
        </ModalHeader>
        <ModalScroll variant={variant}>{children}</ModalScroll>
      </ModalContainer>
    </ModalWrapper>,
    modalRoot
  );
}

Modal.propTypes = {
  children: PropTypes.any.isRequired,
  closeModal: PropTypes.func,
};

export default Modal;

const openModalAnimation = keyframes`
  0% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
`;

const closeModalAnimation = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.8); }
`;
const openModalAnimationStyles = css`
  animation-name: ${openModalAnimation};
  animation-duration: 0.1s;
  animation-fill-mode: both;
`;
const closeModalAnimationStyles = css`
  animation-name: ${closeModalAnimation};
  animation-duration: 0.1s;
  animation-fill-mode: both;
`;

const ModalWrapper = styled.div`
  position: fixed;
  z-index: 200;
  background-color: rgba(0, 0, 0, 0.6);
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  ${({ showModal }) =>
    showModal ? openModalAnimationStyles : closeModalAnimationStyles}
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth}px;
  background-color: white;
  border-radius: 5px;
  max-height: 100%;
  overflow: auto;
  position: relative;
  ${({ smallFullScreen }) =>
    smallFullScreen &&
    `
    @media (max-width: 500px) {
      height: 100%;
    }
    `}
  ${({ variant }) =>
    variant !== "image" &&
    `
    padding-bottom: 25px;
    `}
`;

const ModalScroll = styled.div`
  overflow: auto;
  ${({ variant }) =>
    variant !== "image" &&
    `
    padding: 0 20px 0 20px;
    `}
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  z-index: 1;
  ${({ variant }) =>
    !variant &&
    `
    top: 0;
    right: 0;
    left: 0;
    position: sticky;
    background: white;
    min-height: 20px;
    `}
  ${({ variant }) =>
    variant === "image" &&
    `
    position: absolute;
    top: 5px;
    right: 5px;
    `}
`;

const ModalClose = styled.button`
  cursor: pointer;
  transition: color 0.2s;
  color: ${color.silverChalice};

  &:hover {
    color: ${color.grey};
  }

  ${({ variant }) =>
    !variant &&
    `
    background: none;
    padding: 5px;
    border: none;
    `}

  ${({ variant }) =>
    variant === "image" &&
    `
    padding: 0;
    background-color: ${color.white};
    border-radius: 50%;
    border: 2px solid ${color.silverChalice};
    `}
`;
